import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
}

function getConfirmButtonClass(variant) {
  switch (variant) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    case 'warning':
      return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
    case 'primary':
      return 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500';
    default:
      return 'bg-gray-900 hover:bg-gray-800 focus:ring-gray-700';
  }
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    resolve: null,
  });

  const confirmButtonRef = useRef(null);

  const close = useCallback((result) => {
    setDialog((prev) => {
      if (typeof prev.resolve === 'function') {
        try {
          prev.resolve(!!result);
        } catch (_) {
          // ignore
        }
      }
      return {
        ...prev,
        open: false,
        resolve: null,
      };
    });
  }, []);

  const confirm = useCallback((options = {}) => {
    const {
      title = 'Confirm',
      message = '',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      variant = 'danger',
    } = options;

    return new Promise((resolve) => {
      setDialog({
        open: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        resolve,
      });
    });
  }, []);

  useEffect(() => {
    if (!dialog.open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog.open, close]);

  useEffect(() => {
    if (!dialog.open) return;
    // focus the primary action for keyboard users
    const t = setTimeout(() => {
      confirmButtonRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(t);
  }, [dialog.open]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {dialog.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50" role="presentation">
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-auto"
            role="dialog"
            aria-modal="true"
            aria-label={dialog.title}
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">{dialog.title}</h3>
            {typeof dialog.message === 'string' ? (
              <p className="text-gray-600 mb-6 whitespace-pre-line">{dialog.message}</p>
            ) : (
              <div className="text-gray-600 mb-6">{dialog.message}</div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => close(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {dialog.cancelText}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                onClick={() => close(true)}
                className={`text-white px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${getConfirmButtonClass(dialog.variant)}`}
              >
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
